import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, finalize, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { userIdentifier } from 'src/app/misc/utilities.misc';
import { storageHelper } from 'src/app/misc/storage.misc';
import { AuthService } from './auth.service';
import { UsersService } from './users.service';

type PermissionMode = 'all' | 'any';

@Injectable({
  providedIn: 'root'
})
export class RbacService {
  private permissionCodenamesSubject = new BehaviorSubject<string[]>([]);
  private loadedSubject = new BehaviorSubject<boolean>(false);
  private loadingSubject = new BehaviorSubject<boolean>(false);

  private loadRequest$?: Observable<string[]>;

  public permissionCodenames$ = this.permissionCodenamesSubject.asObservable();
  public loaded$ = this.loadedSubject.asObservable();
  public loading$ = this.loadingSubject.asObservable();

  constructor(
    private usersService: UsersService,
    private authService: AuthService
  ) { }

  loadCurrentUserPermissions(force = false): Observable<string[]> {
    if (!this.authService.isAuthenticated) {
      this.clearPermissions();
      return of([]);
    }

    if (!force && this.loadedSubject.value) {
      return of(this.permissionCodenamesSubject.value);
    }

    if (!force && this.loadRequest$) {
      return this.loadRequest$;
    }

    this.loadingSubject.next(true);

    const request$ = this.resolveCurrentUserId().pipe(
      switchMap((userId) => {
        if (!userId) {
          return of([]);
        }
        return this.usersService.getUserPermissions(userId);
      }),
      map((response: any) => this.normalizePermissionCodenames(response)),
      tap((codenames) => {
        this.permissionCodenamesSubject.next(codenames);
        this.loadedSubject.next(true);
      }),
      catchError(() => {
        this.permissionCodenamesSubject.next([]);
        this.loadedSubject.next(true);
        return of([]);
      }),
      finalize(() => {
        this.loadingSubject.next(false);
        this.loadRequest$ = undefined;
      }),
      shareReplay(1)
    );

    this.loadRequest$ = request$;
    return request$;
  }

  clearPermissions(): void {
    this.permissionCodenamesSubject.next([]);
    this.loadedSubject.next(false);
    this.loadingSubject.next(false);
    this.loadRequest$ = undefined;
  }

  hasPermission(permission: string): boolean {
    if (this.isCurrentUserSuperAdmin()) {
      return true;
    }

    if (!permission) {
      return true;
    }
    return this.permissionCodenamesSubject.value.includes(permission);
  }

  hasPermissions(permissions: string | string[], mode: PermissionMode = 'all'): boolean {
    if (this.isCurrentUserSuperAdmin()) {
      return true;
    }

    const list = this.toPermissionList(permissions);
    if (list.length === 0) {
      return true;
    }

    if (mode === 'any') {
      return list.some((permission) => this.hasPermission(permission));
    }
    return list.every((permission) => this.hasPermission(permission));
  }

  private resolveCurrentUserId(): Observable<number | null> {
    const localUser = storageHelper.local.get(`${userIdentifier}`);
    const localUserId = Number(localUser?.id);
    if (Number.isFinite(localUserId) && localUserId > 0) {
      return of(localUserId);
    }

    return this.authService.userInfo().pipe(
      tap((user) => {
        if (user) {
          storageHelper.local.store(`${userIdentifier}`, user);
        }
      }),
      map((user) => {
        const userId = Number(user?.id);
        return Number.isFinite(userId) && userId > 0 ? userId : null;
      }),
      catchError(() => of(null))
    );
  }

  private normalizePermissionCodenames(response: any): string[] {
    const codenames = new Set<string>();
    this.collectPermissionCodenames(response, codenames);
    return Array.from(codenames);
  }

  private collectPermissionCodenames(source: any, output: Set<string>): void {
    if (!source) {
      return;
    }

    if (Array.isArray(source)) {
      source.forEach((item) => this.collectPermissionCodenames(item, output));
      return;
    }

    if (typeof source === 'string') {
      const codename = source.trim();
      if (codename) {
        output.add(codename);
      }
      return;
    }

    if (typeof source !== 'object') {
      return;
    }

    const codename = String(source?.codename || '').trim();
    if (codename) {
      output.add(codename);
    }

    if (Array.isArray(source?.permissions)) {
      this.collectPermissionCodenames(source.permissions, output);
    }
    if (Array.isArray(source?.groups)) {
      this.collectPermissionCodenames(source.groups, output);
    }
    if (Array.isArray(source?.user_permissions)) {
      this.collectPermissionCodenames(source.user_permissions, output);
    }
    if (Array.isArray(source?.group_permissions)) {
      this.collectPermissionCodenames(source.group_permissions, output);
    }
    if (Array.isArray(source?.items)) {
      this.collectPermissionCodenames(source.items, output);
    }
    if (Array.isArray(source?.results)) {
      this.collectPermissionCodenames(source.results, output);
    }
  }

  private toPermissionList(value: string | string[]): string[] {
    if (Array.isArray(value)) {
      return value.filter((item) => !!item).map((item) => String(item));
    }
    if (typeof value === 'string' && value.trim()) {
      return [value.trim()];
    }
    return [];
  }

  private isCurrentUserSuperAdmin(): boolean {
    const currentUser = storageHelper.local.get(`${userIdentifier}`);
    const roleNames = this.extractRoleNames(currentUser);

    return roleNames.some((roleName) => this.normalizeRoleName(roleName) === 'superadmin');
  }

  private extractRoleNames(user: any): string[] {
    if (!user) {
      return [];
    }

    const roles: any[] = [];

    if (user?.role) {
      roles.push(user.role);
    }

    if (user?.roles) {
      if (Array.isArray(user.roles)) {
        roles.push(...user.roles);
      } else {
        roles.push(user.roles);
      }
    }

    if (user?.group) {
      roles.push(user.group);
    }

    if (Array.isArray(user?.groups)) {
      roles.push(...user.groups);
    }

    const extracted: string[] = [];

    roles.forEach((role) => {
      if (!role) {
        return;
      }

      if (typeof role === 'string') {
        extracted.push(role);
        return;
      }

      if (typeof role === 'object') {
        const values = [role?.name, role?.role, role?.label, role?.codename];
        values.forEach((value) => {
          if (typeof value === 'string' && value.trim()) {
            extracted.push(value);
          }
        });
      }
    });

    if (typeof user?.role_name === 'string' && user.role_name.trim()) {
      extracted.push(user.role_name);
    }

    return extracted;
  }

  private normalizeRoleName(value: any): string {
    return String(value || '')
      .toLowerCase()
      .replace(/[\s_-]+/g, '');
  }
}
